/**
 * Ranked bars: one row per thing (an item, a platform, a country), the name, a thin bar from a
 * shared zero and the value at its tip. One series, one colour (the brand's line colour); length
 * carries the size, so no ramp. Rows can link (to an item). It is a list, so it reads in order
 * without the drawing; the card's table view and CSV carry every column.
 */

export interface Bar {
  key: string;
  label: string;
  value: number;
  /** What the tip says ("1,204", "3 h 20 min"). */
  text: string;
  /** A quiet second line under the name (a share, a date). */
  detail?: string;
  href?: string;
}

export function BarList({ bars, label }: { bars: readonly Bar[]; /** Names the list. */ label: string }) {
  const max = Math.max(0, ...bars.map((b) => b.value));
  return (
    <ol className="flex flex-col" aria-label={label}>
      {bars.map((b) => {
        const share = max > 0 ? b.value / max : 0;
        return (
          <li key={b.key} className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] items-center gap-x-4 gap-y-1 rounded-2xl px-card-inset py-2 max-md:grid-cols-1">
            <div className="min-w-0">
              {b.href ? <a href={b.href} className="block truncate text-body-2 font-medium text-t-primary hover:underline focus-visible:underline">{b.label}</a>
                : <span className="block truncate text-body-2 text-t-primary">{b.label}</span>}
              {b.detail ? <span className="block truncate text-caption text-t-secondary">{b.detail}</span> : null}
            </div>
            <div className="flex min-w-0 items-center gap-3">
              <div className="h-2.5 grow" aria-hidden="true">
                {share > 0 ? <div className="h-full min-w-1 rounded-e-sm bg-accent-stroke" style={{ width: `${share * 100}%` }} /> : null}
              </div>
              <span className="w-24 shrink-0 text-end text-body-2 tabular-nums text-t-primary"><span className="sr-only">{b.label}: </span>{b.text}</span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
